import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ruler, Wrench } from "lucide-react";
import { drw } from "./api";
import { openDrawing } from "./openDrawing";

/** DRW-6 — FOR FM vs FOR INSTALL are unmistakable: colour, icon and word, never just the word. */
export const PURPOSE_STYLE = {
  FM: { label: "FOR FM", chip: "bg-amber-100 text-amber-900 border-amber-400", banner: "bg-amber-500 text-black", Icon: Ruler },
  INSTALL: { label: "FOR INSTALL", chip: "bg-emerald-100 text-emerald-900 border-emerald-500", banner: "bg-emerald-600 text-white", Icon: Wrench },
};

/** One fetch per project of the published shop-sheet links, keyed by room number. */
export function useFieldShopLinks(projectId) {
  const [links, setLinks] = useState({});
  useEffect(() => {
    if (!projectId) return;
    drw.get(`/projects/${projectId}/field-shop/links`).then(({ data }) => setLinks(data.links || {})).catch(() => setLinks({}));
  }, [projectId]);
  return links;
}

export function PurposeChip({ purpose, scopes, size = "xs", testId }) {
  const s = PURPOSE_STYLE[purpose] || PURPOSE_STYLE.INSTALL;
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded border font-bold ${s.chip} ${size === "xs" ? "text-[9px] px-1 py-px" : "text-xs px-2 py-1"}`} data-testid={testId} data-purpose={purpose}>
      <Icon className={size === "xs" ? "w-2.5 h-2.5" : "w-3.5 h-3.5"} />{scopes?.length ? `${scopes.join("/")} ` : ""}{s.label}
    </span>
  );
}

/** The field-shop link beside a room's architectural link. Same funnel (openDrawing: new tab on a
 *  desktop, in place + Back on a handheld), same placement. Nothing → renders nothing; several →
 *  a picker naming scope + purpose for each. RA note (2026-10): a compact "FM" mark — green when
 *  the shop sheet is the current revision, grey when a newer one superseded it; click opens it. */
const FM_MARK = "inline-flex h-4 items-center gap-0.5 rounded border px-1 text-[9px] font-bold leading-none";
const fmTone = (current) => (current ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-gray-300 bg-gray-50 text-gray-500");

export default function FieldShopLinks({ links, roomNumber, projectId, from = "install", vs = "", className = "" }) {
  const navigate = useNavigate();
  const rows = links?.[String(roomNumber || "")] || [];
  const [open, setOpen] = useState(false);
  if (!rows.length) return null;
  const go = (r) => { setOpen(false); openDrawing(navigate, { project: projectId, sheetId: r.sheet_id, room: roomNumber, from, vs }); };
  if (rows.length === 1) {
    const r = rows[0];
    const current = r.is_current !== false;
    return (
      <button type="button" onClick={(e) => { e.stopPropagation(); go(r); }} className={`${FM_MARK} ${fmTone(current)} ${className}`}
              title={`Shop drawing · ${r.sheet_number} — ${r.title}${current ? " (current)" : " (superseded — a newer revision exists)"}`}
              data-testid={`shop-link-${roomNumber}`} data-current={current ? "1" : "0"}>
        <Ruler className="w-2.5 h-2.5" /> FM
      </button>
    );
  }
  const allCurrent = rows.every((r) => r.is_current !== false);
  return (
    <span className={`relative inline-flex ${className}`} onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={() => setOpen((o) => !o)} className={`${FM_MARK} ${fmTone(allCurrent)}`}
              title="Several shop drawings — pick one" data-testid={`shop-link-${roomNumber}`} data-current={allCurrent ? "1" : "0"}>
        <Ruler className="w-2.5 h-2.5" /> FM ×{rows.length}
      </button>
      {open && (
        <div className="absolute z-30 top-full left-0 mt-1 bg-white border rounded-lg shadow-xl p-1 w-60 space-y-1" data-testid={`shop-link-picker-${roomNumber}`}>
          {rows.map((r) => (
            <button key={r.sheet_id} type="button" onClick={() => go(r)} className="w-full text-left flex items-center gap-2 px-2 min-h-11 rounded hover:bg-neutral-50" data-testid={`shop-link-pick-${r.sheet_id}`}>
              <span className={`${FM_MARK} ${fmTone(r.is_current !== false)}`}><Ruler className="w-2.5 h-2.5" /> FM</span>
              <PurposeChip purpose={r.purpose} scopes={r.scopes} />
              <span className="text-[11px] text-neutral-600 truncate">{r.sheet_number}</span>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
